import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RadioGroup, FormControlLabel, Radio, Typography, Box, TextField, Badge, Button } from '@mui/material';
import { Send } from '@mui/icons-material';
import { track } from '@/third-party/MixPanel';

interface FormData {
  readingMode: string;
  question: string;
  notes: string;
}

const ModeSelector: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();



  const [formData, setFormData] = useState<FormData>({
    readingMode: 'in-app',
    question: '',
    notes: ''
  });

  useEffect(() => {
    const localFormData = JSON.parse(localStorage.getItem('readingMetadata') || '{}');
    setFormData(prev => ({
      readingMode: localFormData.readingMode || prev.readingMode,
      question: localFormData.question || prev.question,
      notes: localFormData.notes || prev.notes
    }));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement> | { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    setFormData(updatedFormData);

    localStorage.setItem('readingMetadata', JSON.stringify(updatedFormData));
  };

  const handleModeChange = (value: string) => {
    const updatedFormData = {
      ...formData,
      readingMode: value
    };
    setFormData(updatedFormData);
    localStorage.setItem('readingMetadata', JSON.stringify(updatedFormData));
  };

  const handleReadingClick = () => {

    localStorage.setItem('readingMetadata', JSON.stringify(formData));
    if (formData.question.length === 0) {
      track('spread_question_created', { questionSize: formData.question.length });
    }

    navigate(formData.readingMode === 'in-app' ? '/spread-cards' : '/camera');
  };

  const isFormValid = formData.readingMode;

  return (
    <Box
      className="min-h-screen"
      sx={{
        background: 'linear-gradient(135deg, #F8F1EE 0%, #F5F3F0 100%)',
        padding: '1rem',
        minHeight: '100vh',
      }}
    >


      <Box sx={{ mt: 3, flex: 1 }}>

        <RadioGroup
          value={formData.readingMode}
          onChange={(e) => handleModeChange(e.target.value)}
          sx={{
            display: 'none',
            justifyContent: 'center',
            gap: 1,

            flexDirection: 'column',
            width: '100%',
            '& .MuiFormControlLabel-root': {
              margin: 0,
              border: '1px solid rgba(201, 173, 167, 0.4)',
              borderRadius: '8px',
              padding: '8px 16px',

              transition: 'all 0.3s ease',
              backgroundColor: 'rgba(255,255,255,0.7)',
              flex: 1,
              justifyContent: 'left',
              width: '100%',
              '& .MuiFormControlLabel-label': {
                width: '100%'
              },
              '&:hover': {
                backgroundColor: 'rgba(201, 173, 167, 0.1)',
                transform: 'translateY(-2px)'
              }
            },
            '& .Mui-checked': {
              '& + .MuiFormControlLabel-label': {
                color: 'primary.main',
                fontWeight: 600
              }
            }
          }}
        >
          <FormControlLabel
            value="my-cards"

            control={<Radio color="primary" />}
            label={
              <Box sx={{ opacity: 0.4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography sx={{ fontFamily: '"Playfair Display", serif', textAlign: 'left' }}>
                  {t('modeSelector.physicalCards')}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: 'secondary.light',
                    color: 'secondary.contrastText',
                    fontSize: '0.65rem',
                    padding: '4px 12px',
                    marginLeft: '16px',
                    borderRadius: '16px',
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                    height: '24px',
                    lineHeight: '16px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {t('modeSelector.comingSoon')}
                </Box>
              </Box>
            }
            disabled={true}
            sx={{ width: '100%' }}
          />
          <FormControlLabel
            value="in-app"
            control={<Radio color="primary" />}
            label={
              <Typography sx={{ fontFamily: '"Playfair Display", serif', textAlign: 'center' }}>
                {t('modeSelector.digitalCards')}
              </Typography>
            }
            sx={{ width: '100%' }}
          />

        </RadioGroup>
      </Box>

      <Box sx={{
        position: 'fixed',
        bottom: 60,
        left: 0,
        right: 0,
        p: 2,
        background: 'linear-gradient(135deg, #F8F1EE 0%, #F5F3F0 100%)',
        borderTop: '1px solid rgba(201, 173, 167, 0.4)',
      }}>
        <Typography
          variant="h5"
          sx={{
            fontFamily: '"Playfair Display", serif',
            color: 'primary.main',
            marginBottom: '1rem',
            textAlign: 'center'
          }}
        >
          {t('modeSelector.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>

          <TextField
            autoFocus
            margin="dense"

            type="text"
            fullWidth
            multiline
            minRows={1}
            maxRows={3}
            value={formData.question}
            id="question"
            name="question"
            onChange={(e) => handleChange(e)}
            placeholder={t('modeSelector.questionPlaceholder')}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.7)',
                borderRadius: '12px',
                border: '1px solid rgba(201, 173, 167, 0.4)',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent',
                },
              },
              '& .MuiInputBase-input': {
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: '0.9rem',
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleReadingClick}
            disabled={!isFormValid}
            sx={{
              minWidth: '50px',
              height: '50px',
              marginBottom: "8px",
              borderRadius: '12px',
              bgcolor: '#2B2E4A',
              '&:hover': {
                bgcolor: '#3D4066'
              },
              '&:disabled': {
                bgcolor: 'rgba(43, 46, 74, 0.5)'
              }
            }}
          >
            <Send />
          </Button>
        </Box>
      </Box>
    </Box>


  );
};

export default ModeSelector;
